/**
 * HPFormatBridge - Utility for bridging different HP data formats
 * 
 * This utility handles conversion between different HP data formats:
 * 1. Legacy format: hp (number), maxHp (number)
 * 2. Unified format: hpObj = { current, max }
 * 3. EntityMigrator format: hp = { average, formula }
 * 
 * It ensures consistent HP representation across the application
 * regardless of the source format.
 * 
 * date_of_change: 2025-08-22
 * - Initial implementation with basic format conversion
 * date_of_change: 2025-08-25
 * - Enhanced to standardize HP structure using {current, max} format
 * - Added full migration utilities for consistent HP representation
 * - Added validation and HP integrity verification
 */

/**
 * Creates a unified hpObj from any supported HP format
 * @param {Object|number} entity - Entity or HP value to normalize
 * @returns {Object} Unified { current, max } HP object
 */
export function createUnifiedHP(entity) {
  // Handle direct number input
  if (typeof entity === 'number') {
    return {
      current: entity,
      max: entity
    };
  }
  
  // Handle null/undefined
  if (!entity) {
    return {
      current: 0,
      max: 0
    };
  }
  
  // Already in unified format
  if (entity.hpObj && typeof entity.hpObj.current === 'number') {
    return {
      current: entity.hpObj.current,
      max: entity.hpObj.max || entity.hpObj.current
    };
  }
  
  // EntityMigrator format: { average, formula }
  if (entity.hp && typeof entity.hp === 'object' && entity.hp.average !== undefined) {
    return {
      current: entity.hp.average,
      max: entity.hp.average
    };
  }
  
  // Legacy separate fields
  if (typeof entity.hp === 'number') {
    return {
      current: entity.hp,
      max: entity.maxHp !== undefined ? entity.maxHp : entity.hp
    };
  }
  
  // Nested hp within data (common in entity structure)
  if (entity.data) {
    if (entity.data.hpObj && typeof entity.data.hpObj.current === 'number') {
      return {
        current: entity.data.hpObj.current,
        max: entity.data.hpObj.max || entity.data.hpObj.current
      };
    }
    
    if (entity.data.hp) {
      // EntityMigrator format in data
      if (typeof entity.data.hp === 'object' && entity.data.hp.average !== undefined) {
        return {
          current: entity.data.hp.average,
          max: entity.data.hp.average
        };
      }
      
      // Legacy format in data
      if (typeof entity.data.hp === 'number') {
        return {
          current: entity.data.hp,
          max: entity.data.maxHp !== undefined ? entity.data.maxHp : entity.data.hp
        };
      }
    }
  }
  
  // Default fallback
  return {
    current: 0,
    max: 0
  };
}

/**
 * Updates an entity with unified HP format while preserving existing formats
 * @param {Object} entity - The entity to update
 * @param {Object} hpObj - The HP object { current, max }
 * @returns {Object} Updated entity
 */
export function updateEntityHP(entity, hpObj) {
  if (!entity || typeof entity !== 'object') return entity;
  
  const updatedEntity = { ...entity };
  
  // Update unified format
  updatedEntity.hpObj = { ...hpObj };
  
  // Update legacy fields for compatibility
  updatedEntity.hp = hpObj.current;
  updatedEntity.maxHp = hpObj.max;
  
  // If entity has EntityMigrator format, preserve it but update average
  if (updatedEntity.hp && typeof updatedEntity.hp === 'object' && updatedEntity.hp.formula) {
    updatedEntity.hp.average = hpObj.current;
  }
  
  // If entity has nested data structure, update that too
  if (updatedEntity.data) {
    updatedEntity.data = {
      ...updatedEntity.data,
      hp: updatedEntity.data.hp && typeof updatedEntity.data.hp === 'object' && updatedEntity.data.hp.formula
        ? { ...updatedEntity.data.hp, average: hpObj.current }
        : hpObj.current,
      maxHp: hpObj.max,
      hpObj: { ...hpObj }
    };
  }
  
  return updatedEntity;
}

/**
 * Checks if HP values in an entity are consistent across formats
 * @param {Object} entity - Entity to check
 * @returns {boolean} True if HP values are consistent
 */
export function checkHPConsistency(entity) {
  if (!entity || typeof entity !== 'object') return false;
  
  // Extract values from different formats
  const unifiedCurrent = entity.hpObj?.current;
  const legacyHP = entity.hp;
  
  // If no HP values exist, it's technically consistent
  if (unifiedCurrent === undefined && legacyHP === undefined) {
    return true;
  }
  
  // Check if unified format exists
  if (unifiedCurrent === undefined) {
    return false;
  }
  
  // Check consistency with legacy format
  if (typeof legacyHP === 'number' && legacyHP !== unifiedCurrent) {
    return false;
  }
  
  // Check consistency with EntityMigrator format
  if (typeof legacyHP === 'object' && legacyHP.average !== unifiedCurrent) {
    return false;
  }
  
  // Check consistency with data structure if present
  if (entity.data) {
    const dataUnifiedCurrent = entity.data.hpObj?.current;
    const dataLegacyHP = entity.data.hp;
    
    if (dataUnifiedCurrent !== undefined && dataUnifiedCurrent !== unifiedCurrent) {
      return false;
    }
    
    if (typeof dataLegacyHP === 'number' && dataLegacyHP !== unifiedCurrent) {
      return false;
    }
    
    if (typeof dataLegacyHP === 'object' && dataLegacyHP.average !== unifiedCurrent) {
      return false;
    }
  }
  
  return true;
}

/**
 * Enforces the unified HP format as the primary HP representation
 * and removes legacy format fields to prevent inconsistency
 * @param {Object} entity - Entity to standardize
 * @returns {Object} Entity with standardized HP structure
 */
export function standardizeHPFormat(entity) {
  if (!entity || typeof entity !== 'object') return entity;
  
  // Create a copy to avoid mutating the original
  const standardized = { ...entity };
  
  // Get unified HP from whatever format exists
  const unifiedHP = createUnifiedHP(entity);
  
  // Set the unified format as the only HP representation
  standardized.hpObj = { ...unifiedHP };
  
  // Remove legacy formats to prevent inconsistency
  delete standardized.hp;
  delete standardized.maxHp;
  
  // Handle nested data structure if present
  if (standardized.data) {
    standardized.data = { ...standardized.data };
    standardized.data.hpObj = { ...unifiedHP };
    delete standardized.data.hp;
    delete standardized.data.maxHp;
  }
  
  return standardized;
}

/**
 * Bulk migrates an array of entities to standardized HP format
 * @param {Array} entities - Array of entities to migrate
 * @returns {Array} Migrated entities
 */
export function migrateEntitiesToStandardHP(entities) {
  if (!Array.isArray(entities)) return [];
  
  return entities.map(entity => standardizeHPFormat(entity));
}

/**
 * Extracts current HP value from any HP format safely
 * @param {Object|number} entity - Entity or HP value
 * @returns {number} Current HP value
 */
export function getCurrentHP(entity) {
  const unified = createUnifiedHP(entity);
  return unified.current;
}

/**
 * Extracts max HP value from any HP format safely
 * @param {Object|number} entity - Entity or HP value
 * @returns {number} Max HP value
 */
export function getMaxHP(entity) {
  const unified = createUnifiedHP(entity);
  return unified.max;
}

/**
 * Verifies if an entity is using the standardized HP format
 * @param {Object} entity - Entity to check
 * @returns {boolean} True if entity uses standardized format
 */
export function isStandardizedHPFormat(entity) {
  if (!entity || typeof entity !== 'object') return false;
  
  // Must have hpObj with current and max
  if (!entity.hpObj || typeof entity.hpObj !== 'object') return false;
  if (typeof entity.hpObj.current !== 'number') return false;
  if (typeof entity.hpObj.max !== 'number') return false;
  
  // Must not have legacy formats
  if (entity.hp !== undefined || entity.maxHp !== undefined) return false;
  
  // Check nested data if present
  if (entity.data) {
    if (!entity.data.hpObj || typeof entity.data.hpObj !== 'object') return false;
    if (typeof entity.data.hpObj.current !== 'number') return false;
    if (typeof entity.data.hpObj.max !== 'number') return false;
    if (entity.data.hp !== undefined || entity.data.maxHp !== undefined) return false;
  }
  
  return true;
}

/**
 * Gets the HP object from any entity format
 * @param {Object} entity - Entity to extract HP from
 * @returns {Object} The unified HP object with current and max properties
 */
export function getHPFromEntity(entity) {
  return createUnifiedHP(entity);
}

const HPFormatBridge = {
  createUnifiedHP,
  updateEntityHP,
  checkHPConsistency,
  standardizeHPFormat,
  migrateEntitiesToStandardHP,
  getCurrentHP,
  getMaxHP,
  isStandardizedHPFormat,
  getHPFromEntity
};

export default HPFormatBridge;
